import { useState, useEffect } from "react";
import { 
  DollarSign, 
  Plus, 
  Trash2, 
  Send, 
  FileText,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Receipt,
  ExternalLink
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const incomeCategories = [
  { value: "tattoo_revenue", label: "Tattoo Revenue" },
  { value: "music_revenue", label: "Music Revenue" },
  { value: "video_production", label: "Video Production" },
  { value: "brand_deals", label: "Brand Deals" },
  { value: "merch", label: "Merch" },
  { value: "streaming", label: "Streaming" },
  { value: "sponsorship", label: "Sponsorship" },
  { value: "other", label: "Other" }
];

const expenseCategories = [
  { value: "supplies", label: "Supplies" },
  { value: "equipment", label: "Equipment" },
  { value: "gas", label: "Gas" },
  { value: "marketing_ads", label: "Marketing Ads" },
  { value: "subscriptions", label: "Subscriptions" },
  { value: "payroll", label: "Payroll" },
  { value: "rent", label: "Rent" },
  { value: "fees", label: "Fees" },
  { value: "legal_accounting", label: "Legal/Accounting" }
];

const divisions = [
  { value: "tattoo", label: "Tattoo" },
  { value: "music", label: "Music" },
  { value: "video", label: "Video" },
  { value: "brand", label: "Brand" }
];

const Money = ({ user }) => {
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIncomeDialog, setShowIncomeDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);

  // Form states
  const [incomeForm, setIncomeForm] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: "",
    category: "other",
    division: "brand",
    client_name: "",
    description: ""
  });

  const [expenseForm, setExpenseForm] = useState({
    date: new Date().toISOString().split('T')[0],
    vendor: "",
    amount: "",
    category: "supplies",
    description: ""
  });

  const [invoiceForm, setInvoiceForm] = useState({
    client_id: "",
    client_name: "",
    client_email: "",
    due_date: "",
    items: [{ description: "", quantity: 1, price: 0 }],
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [incomeRes, expenseRes, invoiceRes, clientRes] = await Promise.all([
        fetch(`${API}/money/income`, { credentials: "include" }),
        fetch(`${API}/money/expenses`, { credentials: "include" }),
        fetch(`${API}/money/invoices`, { credentials: "include" }),
        fetch(`${API}/clients`, { credentials: "include" })
      ]);

      if (incomeRes.ok) {
        const data = await incomeRes.json();
        setIncome(data?.income ?? data ?? []);
      } else {
        setIncome([]);
      }
      if (expenseRes.ok) {
        const data = await expenseRes.json();
        setExpenses(data?.expenses ?? data ?? []);
      } else {
        setExpenses([]);
      }
      if (invoiceRes.ok) {
        const data = await invoiceRes.json();
        setInvoices(data?.invoices ?? data ?? []);
      } else {
        setInvoices([]);
      }
      if (clientRes.ok) {
        const data = await clientRes.json();
        setClients(data?.clients ?? data ?? []);
      } else {
        setClients([]);
      }
    } catch {
      toast.error("Failed to load data");
      setIncome([]);
      setExpenses([]);
      setInvoices([]);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const handleAddIncome = async () => {
    try {
      const response = await fetch(`${API}/money/income`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...incomeForm, amount: parseFloat(incomeForm.amount) })
      });

      if (response.ok) {
        toast.success("Income added successfully");
        setShowIncomeDialog(false);
        setIncomeForm({
          date: new Date().toISOString().split('T')[0],
          amount: "",
          category: "other",
          division: "brand",
          client_name: "",
          description: ""
        });
        fetchData();
      } else {
        toast.error("Failed to add income");
      }
    } catch (error) {
      toast.error("Error adding income");
    }
  };

  const handleAddExpense = async () => {
    try {
      const response = await fetch(`${API}/money/expenses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...expenseForm, amount: parseFloat(expenseForm.amount) })
      });

      if (response.ok) {
        toast.success("Expense added successfully");
        setShowExpenseDialog(false);
        setExpenseForm({
          date: new Date().toISOString().split('T')[0],
          vendor: "",
          amount: "",
          category: "supplies",
          description: ""
        });
        fetchData();
      } else {
        toast.error("Failed to add expense");
      }
    } catch (error) {
      toast.error("Error adding expense");
    }
  };

  const handleCreateInvoice = async () => {
    try {
      const subtotal = (invoiceForm.items || []).reduce((sum, item) => sum + ((item.quantity || 0) * (item.price || 0)), 0);
      const invoiceData = {
        ...invoiceForm,
        subtotal,
        total: subtotal,
        status: "draft"
      };

      const response = await fetch(`${API}/money/invoices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(invoiceData)
      });

      if (response.ok) {
        toast.success("Invoice created successfully");
        setShowInvoiceDialog(false);
        setInvoiceForm({
          client_id: "",
          client_name: "",
          client_email: "",
          due_date: "",
          items: [{ description: "", quantity: 1, price: 0 }],
          notes: ""
        });
        fetchData();
      } else {
        toast.error("Failed to create invoice");
      }
    } catch (error) {
      toast.error("Error creating invoice");
    }
  };

  const handleDeleteIncome = async (id) => {
    if (!window.confirm("Delete this income entry?")) return;
    try {
      await fetch(`${API}/money/income/${id}`, {
        method: "DELETE",
        credentials: "include"
      });
      toast.success("Income deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await fetch(`${API}/money/expenses/${id}`, {
        method: "DELETE",
        credentials: "include"
      });
      toast.success("Expense deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleSendInvoice = async (invoice) => {
    try {
      const response = await fetch(`${API}/payments/create-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          invoice_id: invoice.id,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Payment link created!");
        fetchData();
        // Copy link to clipboard
        navigator.clipboard.writeText(data.url);
        toast.info("Payment link copied to clipboard");
      } else {
        toast.error("Failed to create payment link");
      }
    } catch (error) {
      toast.error("Error creating payment link");
    }
  };

  const addInvoiceItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      items: [...invoiceForm.items, { description: "", quantity: 1, price: 0 }]
    });
  };

  const updateInvoiceItem = (index, field, value) => {
    const newItems = [...invoiceForm.items];
    newItems[index][field] = field === 'quantity' || field === 'price' ? parseFloat(value) || 0 : value;
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "badge bg-[#71717A]/20 text-[#A1A1AA]",
      sent: "badge bg-[#3B82F6]/20 text-[#3B82F6]",
      paid: "badge bg-[#22C55E]/20 text-[#22C55E]",
      overdue: "badge bg-[#EF4444]/20 text-[#EF4444]"
    };
    return <span className={styles[status] || styles.draft}>{status}</span>;
  };

  // Calculate totals
  const totalIncome = (income || []).reduce((sum, i) => sum + (i.amount || 0), 0);
  const totalExpenses = (expenses || []).reduce((sum, e) => sum + (e.amount || 0), 0);
  const netProfit = totalIncome - totalExpenses;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="money-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA]">Money</h1>
          <p className="text-[#A1A1AA] mt-1">Track your income, expenses, and invoices</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="stat-card" data-testid="total-income-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#22C55E]/10 flex items-center justify-center">
                <TrendingUp className="text-[#22C55E]" size={24} />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Total Income</p>
                <p className="text-2xl font-bold text-[#22C55E]">{formatCurrency(totalIncome)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="total-expenses-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#EF4444]/10 flex items-center justify-center">
                <TrendingDown className="text-[#EF4444]" size={24} />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Total Expenses</p>
                <p className="text-2xl font-bold text-[#EF4444]">{formatCurrency(totalExpenses)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card gold-glow" data-testid="net-profit-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <DollarSign className="text-[#D4AF37]" size={24} />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Net Profit</p>
                <p className={`text-2xl font-bold ${netProfit >= 0 ? 'text-[#D4AF37]' : 'text-[#EF4444]'}`}>
                  {formatCurrency(netProfit)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="income" className="space-y-4">
        <TabsList className="bg-[#121214] border border-[#27272A]">
          <TabsTrigger value="income" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
            Income
          </TabsTrigger>
          <TabsTrigger value="expenses" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
            Expenses
          </TabsTrigger>
          <TabsTrigger value="invoices" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
            Invoices
          </TabsTrigger>
        </TabsList>

        {/* Income Tab */}
        <TabsContent value="income">
          <Card className="bg-[#121214] border-[#27272A]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-[#FAFAFA]">Income Entries</CardTitle>
              <Dialog open={showIncomeDialog} onOpenChange={setShowIncomeDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-gold" data-testid="add-income-btn">
                    <Plus size={18} className="mr-2" /> Add Income
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#121214] border-[#27272A]">
                  <DialogHeader>
                    <DialogTitle className="text-[#FAFAFA]">Add Income</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[#A1A1AA]">Date</Label>
                        <Input
                          type="date"
                          value={incomeForm.date}
                          onChange={(e) => setIncomeForm({ ...incomeForm, date: e.target.value })}
                          className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                        />
                      </div>
                      <div>
                        <Label className="text-[#A1A1AA]">Amount ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={incomeForm.amount}
                          onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })}
                          placeholder="0.00"
                          className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[#A1A1AA]">Category</Label>
                        <Select value={incomeForm.category} onValueChange={(v) => setIncomeForm({ ...incomeForm, category: v })}>
                          <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                            {incomeCategories.map((cat) => (
                              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[#A1A1AA]">Division</Label>
                        <Select value={incomeForm.division} onValueChange={(v) => setIncomeForm({ ...incomeForm, division: v })}>
                          <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                            {divisions.map((div) => (
                              <SelectItem key={div.value} value={div.value}>{div.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Client Name (Optional)</Label>
                      <Input
                        value={incomeForm.client_name}
                        onChange={(e) => setIncomeForm({ ...incomeForm, client_name: e.target.value })}
                        placeholder="Client name"
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Description</Label>
                      <Input
                        value={incomeForm.description}
                        onChange={(e) => setIncomeForm({ ...incomeForm, description: e.target.value })}
                        placeholder="Brief description"
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                    <Button onClick={handleAddIncome} className="w-full btn-gold" data-testid="submit-income-btn">
                      Add Income
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Category</th>
                      <th>Division</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {income.map((entry) => (
                      <tr key={entry.id}>
                        <td className="text-[#FAFAFA]">{entry.date}</td>
                        <td className="text-[#A1A1AA]">{entry.description || entry.client_name || '-'}</td>
                        <td><span className="badge badge-gold capitalize">{entry.category?.replace('_', ' ')}</span></td>
                        <td className="text-[#A1A1AA] capitalize">{entry.division}</td>
                        <td className="text-[#22C55E] font-medium">{formatCurrency(entry.amount)}</td>
                        <td>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteIncome(entry.id)} className="text-[#EF4444] hover:bg-[#EF4444]/10">
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {income.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center text-[#71717A] py-8">No income entries yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses">
          <Card className="bg-[#121214] border-[#27272A]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-[#FAFAFA]">Expense Entries</CardTitle>
              <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-gold" data-testid="add-expense-btn">
                    <Plus size={18} className="mr-2" /> Add Expense
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#121214] border-[#27272A]">
                  <DialogHeader>
                    <DialogTitle className="text-[#FAFAFA]">Add Expense</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[#A1A1AA]">Date</Label>
                        <Input
                          type="date"
                          value={expenseForm.date}
                          onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                          className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                        />
                      </div>
                      <div>
                        <Label className="text-[#A1A1AA]">Amount ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={expenseForm.amount}
                          onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                          placeholder="0.00"
                          className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Vendor</Label>
                      <Input
                        value={expenseForm.vendor}
                        onChange={(e) => setExpenseForm({ ...expenseForm, vendor: e.target.value })}
                        placeholder="Vendor name"
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Category</Label>
                      <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
                        <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                          {expenseCategories.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Description</Label>
                      <Input
                        value={expenseForm.description}
                        onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                        placeholder="Brief description"
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                    <Button onClick={handleAddExpense} className="w-full btn-gold" data-testid="submit-expense-btn">
                      Add Expense
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Vendor</th>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((entry) => (
                      <tr key={entry.id}>
                        <td className="text-[#FAFAFA]">{entry.date}</td>
                        <td className="text-[#FAFAFA]">{entry.vendor}</td>
                        <td><span className="badge bg-[#EF4444]/20 text-[#EF4444] capitalize">{entry.category?.replace('_', ' ')}</span></td>
                        <td className="text-[#A1A1AA]">{entry.description || '-'}</td>
                        <td className="text-[#EF4444] font-medium">-{formatCurrency(entry.amount)}</td>
                        <td>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteExpense(entry.id)} className="text-[#EF4444] hover:bg-[#EF4444]/10">
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {expenses.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center text-[#71717A] py-8">No expense entries yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card className="bg-[#121214] border-[#27272A]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-[#FAFAFA]">Invoices</CardTitle>
              <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-gold" data-testid="create-invoice-btn">
                    <Plus size={18} className="mr-2" /> Create Invoice
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#121214] border-[#27272A] max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-[#FAFAFA]">Create Invoice</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[#A1A1AA]">Client</Label>
                        <Select 
                          value={invoiceForm.client_id} 
                          onValueChange={(v) => {
                            const client = clients.find(c => c.id === v);
                            setInvoiceForm({ 
                              ...invoiceForm, 
                              client_id: v, 
                              client_name: client?.name || "",
                              client_email: client?.email || ""
                            });
                          }}
                        >
                          <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                            <SelectValue placeholder="Select client" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[#A1A1AA]">Due Date</Label>
                        <Input
                          type="date"
                          value={invoiceForm.due_date}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                          className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-[#A1A1AA]">Line Items</Label>
                      <div className="space-y-2 mt-2">
                        {invoiceForm.items.map((item, idx) => (
                          <div key={idx} className="grid grid-cols-12 gap-2">
                            <Input
                              placeholder="Description"
                              value={item.description}
                              onChange={(e) => updateInvoiceItem(idx, 'description', e.target.value)}
                              className="col-span-6 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                            />
                            <Input
                              type="number"
                              placeholder="Qty"
                              value={item.quantity}
                              onChange={(e) => updateInvoiceItem(idx, 'quantity', e.target.value)}
                              className="col-span-2 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                            />
                            <Input
                              type="number"
                              placeholder="Price"
                              value={item.price}
                              onChange={(e) => updateInvoiceItem(idx, 'price', e.target.value)}
                              className="col-span-3 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                            />
                            <span className="col-span-1 flex items-center justify-end text-[#A1A1AA]">
                              {formatCurrency(item.quantity * item.price)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <Button variant="outline" size="sm" onClick={addInvoiceItem} className="mt-2 border-[#27272A] text-[#A1A1AA]">
                        <Plus size={14} className="mr-1" /> Add Line
                      </Button>
                    </div>

                    <div className="flex justify-end">
                      <div className="text-right">
                        <p className="text-[#A1A1AA]">Total</p>
                        <p className="text-2xl font-bold text-[#D4AF37]">
                          {formatCurrency(invoiceForm.items.reduce((sum, item) => sum + (item.quantity * item.price), 0))}
                        </p>
                      </div>
                    </div>

                    <Button onClick={handleCreateInvoice} className="w-full btn-gold" data-testid="submit-invoice-btn">
                      Create Invoice
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Client</th>
                      <th>Due Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="text-[#FAFAFA] font-medium">{invoice.invoice_number}</td>
                        <td className="text-[#FAFAFA]">{invoice.client_name}</td>
                        <td className="text-[#A1A1AA]">{invoice.due_date}</td>
                        <td className="text-[#D4AF37] font-medium">{formatCurrency(invoice.total)}</td>
                        <td>{getStatusBadge(invoice.status)}</td>
                        <td>
                          <div className="flex gap-2">
                            {invoice.status !== 'paid' && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleSendInvoice(invoice)}
                                className="text-[#D4AF37] hover:bg-[#D4AF37]/10"
                                data-testid={`send-invoice-${invoice.id}`}
                              >
                                <CreditCard size={16} className="mr-1" />
                                Payment Link
                              </Button>
                            )}
                            {invoice.payment_link && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => window.open(invoice.payment_link, '_blank')}
                                className="text-[#3B82F6] hover:bg-[#3B82F6]/10"
                              >
                                <ExternalLink size={16} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {invoices.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center text-[#71717A] py-8">No invoices yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Money;
